/**
 * 流程版本节点 option / graph 规范化：编辑器保存 ↔ 发起页加载共用
 * 避免 JSON 字符串、related 挂载位置、独立 acls 表导致字段丢失
 */

export const NODE_OPTION_RECOVER_KEYS = [
  'name',
  'view_id',
  'acl',
  'clonedBy',
  'clonedType',
  'clonedKey',
  'cloneBy',
  'clonedDefaultUsers',
  'parallelVisible',
  'independentExecutors',
  'opinionMode',
  'enableOpinion',
  'weak',
  'multiple',
  'executor-type',
  'approval-region',
  'default-users',
  'executor-region',
  'timeout',
  'timeoutType',
  'message',
  'status',
  'users',
];

export function parseMaybeJson(value) {
  if (typeof value !== 'string') return value;
  const s = value.trim();
  if (!s || (s[0] !== '{' && s[0] !== '[')) return value;
  try {
    return JSON.parse(s);
  } catch (e) {
    return value;
  }
}

function asObject(value, fallback = {}) {
  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return parsed;
  }
  return fallback;
}

export function normalizeNodeRecord(node) {
  if (!node || typeof node !== 'object') return node;
  let option = asObject(node.option, {});
  let graph = asObject(node.graph, {});
  if (!graph.type && (node.type || node.graph && node.graph.type)) {
    graph = {
      x: node.x,
      y: node.y,
      type: node.type,
      text: node.text,
      ...graph,
    };
  }
  if (!Object.keys(option).length && node.properties) {
    option = asObject(node.properties, {});
  }
  return {
    ...node,
    option,
    graph,
  };
}

export function normalizeActionRecord(action) {
  if (!action || typeof action !== 'object') return action;
  return {
    ...action,
    option: asObject(action.option, action.option && typeof action.option === 'object' ? action.option : {}),
    graph: asObject(action.graph, action.graph && typeof action.graph === 'object' ? action.graph : {}),
  };
}

function aclFromRelatedRow(row) {
  if (!row || typeof row !== 'object') return null;
  if (row.acl != null) return parseMaybeJson(row.acl);
  if (row.option != null) {
    const opt = parseMaybeJson(row.option);
    if (opt && typeof opt === 'object' && opt.acl != null) return opt.acl;
    if (opt && typeof opt === 'object') return opt;
  }
  if (row.data != null) return parseMaybeJson(row.data);
  return null;
}

export function mergeAclsIntoNodes(version) {
  if (!version || !Array.isArray(version.nodes) || !Array.isArray(version.acls)) {
    return version;
  }
  const acls = version.acls;
  if (!acls.length) return version;
  version.nodes.forEach((node) => {
    if (!node) return;
    if (!node.option || typeof node.option !== 'object') {
      node.option = {};
    }
    if (node.option.acl != null) return;
    const hit = acls.find((row) => {
      const nid = row && (row.node_id || row.flow_node_id || row.target_id || row.id);
      return nid != null && String(nid) === String(node.id);
    });
    if (!hit) return;
    const acl = aclFromRelatedRow(hit);
    if (acl != null) {
      node.option.acl = acl;
    }
  });
  return version;
}

/** 把 API 版本记录整理成 create / 运行时可用的 nodes[].option + actions */
export function normalizeVersion(raw) {
  if (!raw || typeof raw !== 'object') {
    return {nodes: [], actions: []};
  }
  const version = {
    ...raw,
    nodes: Array.isArray(raw.nodes) ? raw.nodes.map(normalizeNodeRecord) : [],
    actions: Array.isArray(raw.actions) ? raw.actions.map(normalizeActionRecord) : [],
    acls: Array.isArray(raw.acls) ? raw.acls : [],
  };
  return mergeAclsIntoNodes(version);
}

/**
 * 发起页：nodes/actions 可能挂在 flow.activeVersion，也可能挂在 flow 根上
 */
export function versionFromFlow(flow) {
  const src = flow && typeof flow === 'object' ? flow : {};
  const nested = src.activeVersion || src.active_version || {};
  return normalizeVersion({
    ...nested,
    nodes: nested.nodes || src.nodes || [],
    actions: nested.actions || src.actions || [],
    acls: nested.acls || src.acls || [],
  });
}

/** 保存时：当前 properties 覆盖已加载 option，空缺字段从 origin 补回 */
export function mergeNodeOption(originOption, properties) {
  const origin = asObject(originOption, {});
  const props = asObject(properties, {});
  const option = {...origin, ...props};
  NODE_OPTION_RECOVER_KEYS.forEach((key) => {
    if (option[key] == null && origin[key] != null) {
      option[key] = origin[key];
    }
  });
  delete option._execStatus;
  delete option._clonePartialDone;
  return option;
}

function configNodeType(n) {
  return (n && n.graph && n.graph.type) || (n && n.type) || '';
}

function insertCarbonAfterHost(level, fromId, carbon) {
  let hostIdx = -1;
  for (let i = 0; i < level.length; i++) {
    if (level[i] && level[i].id === fromId) hostIdx = i;
  }
  if (hostIdx < 0) {
    level.push(carbon);
    return;
  }
  let insertAt = hostIdx + 1;
  while (insertAt < level.length && configNodeType(level[insertAt]) === 'carbon_node') {
    insertAt += 1;
  }
  level.splice(insertAt, 0, carbon);
}

/**
 * 发起/测试配置分层：抄送挂在来源节点后面，不与后续审批并列为下一层
 */
export function buildConfigNodeLevels(nodeList, actionList) {
  const nodes = Array.isArray(nodeList) ? nodeList : [];
  const actions = Array.isArray(actionList) ? actionList : [];
  const start = nodes.find((n) => configNodeType(n) === 'start_node');
  if (!start) return [];

  const levels = [[start]];
  const visited = new Set([start.id]);
  let frontier = [start.id];

  const levelOf = (id) => {
    for (let i = 0; i < levels.length; i++) {
      if (levels[i].some((n) => n && n.id === id)) return levels[i];
    }
    return null;
  };

  while (frontier.length) {
    const levelNodes = [];
    const nextFrontier = [];
    actions.forEach((a) => {
      const from = a.from || a.sourceNodeId;
      const to = a.to || a.targetNodeId;
      if (!frontier.includes(from) || !to) return;
      const n = nodes.find((node) => node && node.id == to);
      if (!n || visited.has(n.id)) return;
      visited.add(n.id);
      const t = configNodeType(n);
      if (t === 'carbon_node') {
        const host = levelOf(from);
        if (host) insertCarbonAfterHost(host, from, n);
        else levelNodes.push(n);
        return;
      }
      if (t === 'finish_node') return;
      levelNodes.push(n);
      nextFrontier.push(n.id);
    });
    if (!levelNodes.length) break;
    levels.push(levelNodes);
    frontier = nextFrontier;
  }
  return levels;
}
